import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useInstallPrompt } from '../lib/installPrompt';
import { InstallHelpSheet } from './InstallHelpSheet';
import { Modal } from './Modal';
import { usePlan } from './plan/usePlan';
import {
  GLYPH_DOWNLOAD,
  GLYPH_LAMP,
  GLYPH_SEARCH,
  GLYPH_SIGN_OUT,
  linksForPlan,
  MORE_LINKS,
} from './shellNav';
import { useTheme } from './useTheme';

interface MoreSheetProps {
  /** The signed-in address, shown because the phone has no header row. */
  email: string | null;
  /** Close and hand focus back to the More button. */
  onDismiss: () => void;
  /** Close without moving focus: a link was followed. */
  onNavigate: () => void;
  onOpenPalette: () => void;
  onSignOut: () => void;
}

/**
 * The phone's fifth tab: the destinations that did not fit the bar
 * (Templates, Assistant, News, Knowledge base, Settings), the command palette
 * for a device with no Ctrl K, the theme switch, Install app (only where the
 * phone can install and has not; iOS gets the Share steps) and Sign out — everything the
 * desktop header and sidebar hold that the phone shell hides.
 *
 * A `Modal`, so a sheet from the bottom edge on a phone with the platform's
 * focus trap and Escape. `AppLayout` closes it on any navigation.
 */
export function MoreSheet({
  email,
  onDismiss,
  onNavigate,
  onOpenPalette,
  onSignOut,
}: MoreSheetProps) {
  const { theme, setPreference } = useTheme();
  const { can } = usePlan();
  const nextTheme = theme === 'dark' ? 'light' : 'dark';
  const install = useInstallPrompt();
  const [isInstallHelpOpen, setIsInstallHelpOpen] = useState(false);

  return (
    <Modal title="More" onDismiss={onDismiss}>
      {email ? <p className="more-sheet__email">{email}</p> : null}
      <ul className="more-sheet__list">
        {linksForPlan(MORE_LINKS, can).map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                isActive ? 'more-sheet__row more-sheet__row--active' : 'more-sheet__row'
              }
              onClick={onNavigate}
            >
              <span className="more-sheet__icon" aria-hidden="true">
                {String.fromCodePoint(item.icon)}
              </span>
              <span>{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
      <ul className="more-sheet__list more-sheet__list--actions">
        <li>
          <button className="more-sheet__row" type="button" onClick={onOpenPalette}>
            <span className="more-sheet__icon" aria-hidden="true">
              {GLYPH_SEARCH}
            </span>
            <span>Search commands</span>
          </button>
        </li>
        <li>
          <button
            className={
              theme === 'light' ? 'more-sheet__row more-sheet__row--lit' : 'more-sheet__row'
            }
            type="button"
            onClick={() => setPreference(nextTheme)}
          >
            <span className="more-sheet__icon" aria-hidden="true">
              {GLYPH_LAMP}
            </span>
            <span>Switch to {nextTheme} theme</span>
          </button>
        </li>
        {install.isOffered ? (
          <li>
            <button
              className="more-sheet__row"
              type="button"
              onClick={() => {
                if (install.canPrompt) void install.prompt();
                else setIsInstallHelpOpen(true);
              }}
            >
              <span className="more-sheet__icon" aria-hidden="true">
                {GLYPH_DOWNLOAD}
              </span>
              <span>Install app</span>
            </button>
          </li>
        ) : null}
        <li>
          <button className="more-sheet__row" type="button" onClick={onSignOut}>
            <span className="more-sheet__icon" aria-hidden="true">
              {GLYPH_SIGN_OUT}
            </span>
            <span>Sign out</span>
          </button>
        </li>
      </ul>
      {isInstallHelpOpen ? (
        <InstallHelpSheet onDismiss={() => setIsInstallHelpOpen(false)} />
      ) : null}
    </Modal>
  );
}
